import React, { useState } from "react";
import { Menu, MenuItem } from "@mui/material";
import { NavLink } from "react-router-dom";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CampaignIcon from "@mui/icons-material/Campaign";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import SummarizeIcon from "@mui/icons-material/Summarize";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import GroupsIcon from "@mui/icons-material/Groups";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import WebhookIcon from "@mui/icons-material/Webhook";
import BusinessIcon from "@mui/icons-material/Business";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import GroupIcon from "@mui/icons-material/Group";
import StorageIcon from "@mui/icons-material/Storage";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import { useAuth } from "../../context/AuthContext";
import { useAccess } from "../../context/AccessContext";
import { FEATURE_CODES, TENANT_ROLE_CODES } from "../../constants/constants";

interface FeatureSidebarLinkProps {
  available: boolean;
  to: string;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}

function FeatureSidebarLink({
  available,
  to,
  icon,
  label,
  onClick,
}: FeatureSidebarLinkProps) {
  if (!available) {
    return (
      <div
        className="sidebar-link sidebar-link--unavailable"
        aria-disabled="true"
        title={`${label} is not included in your organisation profile`}
      >
        {icon}
        <span>{label}</span>
      </div>
    );
  }

  return (
    <NavLink to={to} className="sidebar-link" onClick={onClick}>
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}

export default function Sidebar() {
  const {
    canAccessAdmin,
    isAuthenticated,
    isServiceManager,
    isSystemAdmin,
    logout,
    selectedTenant,
    switchTenant,
    user,
  } = useAuth();
  const { hasFeature } = useAccess();
  const canUseInvoices =
    canAccessAdmin ||
    user?.tenants
      ?.find((tenant) => tenant.tenantId === selectedTenant?.tenantId)
      ?.roles?.some((role) =>
        [TENANT_ROLE_CODES.SELF_EMPLOYED_STAFF, TENANT_ROLE_CODES.INVOICE_REVIEWER]
          .some((allowed) => allowed.toLowerCase() === role.toLowerCase()),
      );
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [accountMenuAnchor, setAccountMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [isSwitchingTenant, setIsSwitchingTenant] = useState(false);

  const userEmail = user?.email || "Guest";
  const closeMobileMenu = () => setIsMobileOpen(false);
  const closeAccountMenu = () => setAccountMenuAnchor(null);

  const handleLogout = async () => {
    closeAccountMenu();
    closeMobileMenu();
    await logout();
  };

  const handleLoginClick = () => {
    closeAccountMenu();
    closeMobileMenu();
  };

  const handleTenantChange = async (tenantId: number) => {
    setIsSwitchingTenant(true);
    const switched = await switchTenant(tenantId);
    if (!switched) setIsSwitchingTenant(false);
  };

  return (
    <>
      {!isMobileOpen && (
        <button
          type="button"
          className="sidebar-mobile-open-button"
          onClick={() => setIsMobileOpen(true)}
          aria-label="Expand menu"
          aria-expanded={isMobileOpen}
        >
          <MenuIcon />
        </button>
      )}

      {isMobileOpen && (
        <button
          type="button"
          className="sidebar-mobile-backdrop"
          onClick={closeMobileMenu}
          aria-label="Close menu"
        />
      )}

      <aside
        className={`sidebar ${isMobileOpen ? "sidebar--mobile-open" : ""}`}
      >
        <div className="sidebar-logo">
          <button
            type="button"
            className="sidebar-account-button"
            onClick={(event) => setAccountMenuAnchor(event.currentTarget)}
            aria-label={
              isAuthenticated ? "Open account menu" : "Open login menu"
            }
            aria-controls={
              accountMenuAnchor ? "sidebar-account-menu" : undefined
            }
            aria-haspopup="menu"
            aria-expanded={Boolean(accountMenuAnchor)}
          >
            <AccountCircleIcon className="sidebar-logo-user-icon" />
          </button>
          <div>
            <div className="sidebar-logo-title">Frontline Systems</div>
            <div className="sidebar-logo-subtitle">{userEmail}</div>
          </div>
          <button
            type="button"
            className="sidebar-mobile-toggle"
            onClick={() => setIsMobileOpen((prev) => !prev)}
            aria-label={isMobileOpen ? "Collapse menu" : "Expand menu"}
          >
            {isMobileOpen ? <ChevronLeftIcon /> : <MenuIcon />}
          </button>
        </div>

        <Menu
          id="sidebar-account-menu"
          anchorEl={accountMenuAnchor}
          open={Boolean(accountMenuAnchor)}
          onClose={closeAccountMenu}
          PaperProps={{ className: "sidebar-account-menu" }}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <div className="sidebar-account-menu-header">
            <div className="sidebar-account-menu-title">
              {isAuthenticated ? "Signed in as" : "Account"}
            </div>
            <div className="sidebar-account-menu-email">{userEmail}</div>
            {isAuthenticated && (user?.tenants?.length ?? 0) > 0 && (
              <label className="sidebar-account-tenant-selector">
                <span>Active tenant</span>
                <select
                  value={selectedTenant?.tenantId ?? ""}
                  onChange={(event) =>
                    void handleTenantChange(Number(event.target.value))
                  }
                  disabled={isSwitchingTenant}
                  aria-label="Active tenant"
                >
                  {user?.tenants.map((tenant) => (
                    <option key={tenant.tenantId} value={tenant.tenantId}>
                      {tenant.tenantName}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {isAuthenticated ? (
            <MenuItem onClick={handleLogout}>
              <LogoutIcon fontSize="small" />
              <span>Logout</span>
            </MenuItem>
          ) : (
            <MenuItem
              component={NavLink}
              to="/login"
              onClick={handleLoginClick}
            >
              <LoginIcon fontSize="small" />
              <span>Login</span>
            </MenuItem>
          )}
        </Menu>

        <nav className="sidebar-nav">
          {isAuthenticated ? (
            <>
              <div className="sidebar-section-label">Workspace</div>
              <NavLink
                to="/dashboard"
                className="sidebar-link"
                onClick={closeMobileMenu}
              >
                <DashboardIcon className="sidebar-link-icon" />
                <span>Dashboard</span>
              </NavLink>

              <FeatureSidebarLink
                available={hasFeature(FEATURE_CODES.SERVICE_DETAILS)}
                to="/admin/manage-services"
                icon={
                  <MiscellaneousServicesIcon className="sidebar-link-icon" />
                }
                label="Services"
                onClick={closeMobileMenu}
              />

              {(canAccessAdmin || isServiceManager) && (
                <FeatureSidebarLink
                  available={hasFeature(FEATURE_CODES.SERVICE_CALENDAR)}
                  to="/calendar"
                  icon={<CalendarMonthIcon className="sidebar-link-icon" />}
                  label="Calendar"
                  onClick={closeMobileMenu}
                />
              )}

              {canUseInvoices && (
                <NavLink
                  to="/invoices"
                  className="sidebar-link"
                  onClick={closeMobileMenu}
                >
                  <ReceiptLongIcon className="sidebar-link-icon" />
                  <span>Invoices</span>
                </NavLink>
              )}

              {(canAccessAdmin || isServiceManager) && (
                <FeatureSidebarLink
                  available={hasFeature(FEATURE_CODES.SERVICE_REGISTRATION)}
                  to="/admin/public-registrations"
                  icon={<FactCheckIcon className="sidebar-link-icon" />}
                  label="New Registrations"
                  onClick={closeMobileMenu}
                />
              )}

              <div className="sidebar-section-label">Comms</div>
              <FeatureSidebarLink
                available={hasFeature(FEATURE_CODES.MESSAGING_AUDIENCES_COMMS)}
                to="/manage-audiences"
                icon={<GroupsIcon className="sidebar-link-icon" />}
                label="Contact Lists"
                onClick={closeMobileMenu}
              />
              <FeatureSidebarLink
                available={hasFeature(FEATURE_CODES.MESSAGING_AUDIENCES_COMMS)}
                to="/manage-campaigns"
                icon={<CampaignIcon className="sidebar-link-icon" />}
                label="Campaigns"
                onClick={closeMobileMenu}
              />
              <FeatureSidebarLink
                available={hasFeature(FEATURE_CODES.MESSAGING_AUDIENCES_COMMS)}
                to="/conversations"
                icon={<ForumOutlinedIcon className="sidebar-link-icon" />}
                label="Conversations"
                onClick={closeMobileMenu}
              />

              {canAccessAdmin && (
                <>
                  <div className="sidebar-section-label">Reports</div>
                  <NavLink
                    to="/reports"
                    className="sidebar-link"
                    onClick={closeMobileMenu}
                  >
                    <SummarizeIcon className="sidebar-link-icon" />
                    <span>Report Centre</span>
                  </NavLink>
                </>
              )}

              {canAccessAdmin && (
                <>
                  <div className="sidebar-section-label">Organisation</div>
                  <NavLink
                    to="/admin/organisation/details"
                    className="sidebar-link"
                    onClick={closeMobileMenu}
                  >
                    <BusinessIcon className="sidebar-link-icon" />
                    <span>Details</span>
                  </NavLink>

                  <NavLink
                    to="/admin/organisation/people"
                    className="sidebar-link"
                    onClick={closeMobileMenu}
                  >
                    <GroupIcon className="sidebar-link-icon" />
                    <span>People</span>
                  </NavLink>
                </>
              )}

              {isSystemAdmin && (
                <>
                  <div className="sidebar-section-label">
                    System Administration
                  </div>
                  <NavLink
                    to="/admin/system/organisations"
                    className="sidebar-link"
                    onClick={closeMobileMenu}
                  >
                    <ManageAccountsIcon className="sidebar-link-icon" />
                    <span>Manage Organisations</span>
                  </NavLink>
                  <NavLink
                    to="/admin/system/static-data"
                    className="sidebar-link"
                    onClick={closeMobileMenu}
                  >
                    <StorageIcon className="sidebar-link-icon" />
                    <span>Static Data</span>
                  </NavLink>
                  <NavLink
                    to="/admin/community-directory-import/upload"
                    className="sidebar-link"
                    onClick={closeMobileMenu}
                  >
                    <UploadFileIcon className="sidebar-link-icon" />
                    <span>Directory Import</span>
                  </NavLink>

                  <NavLink
                    to="/admin/webhooks"
                    className="sidebar-link"
                    onClick={closeMobileMenu}
                  >
                    <WebhookIcon className="sidebar-link-icon" />
                    <span>Webhooks</span>
                  </NavLink>
                </>
              )}
            </>
          ) : (
            <>
              <NavLink
                to="/login"
                className="sidebar-link"
                onClick={closeMobileMenu}
              >
                <LoginIcon className="sidebar-link-icon" />
                <span>Log In</span>
              </NavLink>
            </>
          )}
        </nav>
      </aside>
    </>
  );
}
